export type OrderPortalBasketItem = {
  productId: string;
  quantity: number;
};

export type OrderPortalBasketChannel = "NOGA" | "DIRECT";

/** Keep in sync with boodil-client `basketDeepLink` caps. */
const MAX_BASKET_DEEP_LINK_ITEMS = 100;
const MAX_BASKET_DEEP_LINK_QUANTITY = 99;
const SAFE_PRODUCT_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Build a deep-link into the client Order Portal basket.
 * Floorplanner only opens this URL — the client shows a confirm step before
 * mutating the basket so a GET alone never changes state.
 */
export function buildOrderPortalBasketUrl(
  clientUri: string | undefined,
  items: OrderPortalBasketItem[],
  options?: { channel?: OrderPortalBasketChannel },
): string | null {
  const base = clientUri?.trim().replace(/\/$/, "");
  if (!base) return null;

  const pairs: string[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    if (pairs.length >= MAX_BASKET_DEEP_LINK_ITEMS) break;
    const productId = item.productId?.trim();
    if (!productId || !SAFE_PRODUCT_ID.test(productId) || seen.has(productId)) {
      continue;
    }
    seen.add(productId);
    const quantity = Math.min(
      MAX_BASKET_DEEP_LINK_QUANTITY,
      Math.max(1, Math.floor(Number(item.quantity) || 1)),
    );
    pairs.push(`${productId}:${quantity}`);
  }

  if (pairs.length === 0) return null;

  const params = new URLSearchParams();
  params.set("add", pairs.join(","));
  if (options?.channel) {
    params.set("channel", options.channel);
  }

  return `${base}/order-portal/basket?${params.toString()}`;
}
